'use client';

import type { MouseEvent, ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import { IconButton } from './icon-button';
import styles from './notification-item.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

export type NotificationItemType = 'job_complete' | 'system_alert' | 'qc_review';

const TYPE_ICON: Record<NotificationItemType, IconName> = {
  job_complete: 'success',
  system_alert: 'warn',
  qc_review: 'clipboard-check',
};

interface NotificationItemProps {
  type: NotificationItemType;
  title: string;
  body?: string | null;
  /** Already-formatted relative time ("2h ago"). */
  timestamp: string;
  isRead: boolean;
  /**
   * Destination for the row. Callers pass an ALREADY-VALIDATED same-origin
   * path (`safeInternalPath`); null means the notification has nowhere to go.
   */
  href?: string | null;
  /**
   * `comfortable` — the inbox: roomier, actions on their own row (phone) or
   * inline (desktop). `compact` — the bell panel: tighter rhythm, hairline
   * separators, no visible action labels.
   */
  density?: 'comfortable' | 'compact';
  /** Mid-dismiss: the row collapses and fades before the owner drops it. */
  isLeaving?: boolean;
  /**
   * Activating the row marks it read, and navigates when there's an `href`.
   * The dismiss × is deliberately outside this target.
   */
  onActivate?: () => void;
  onDismiss?: () => void;
  markReadLabel: string;
  dismissLabel: string;
}

/**
 * NotificationItem — the one notification row. Both the inbox and the bell
 * panel compose it; before this element they were two hand-rolled copies of
 * the same three fields with different padding, icon sizes, and dismiss
 * affordances.
 *
 * Emphasis is inverted from the obvious reading: **unread is the crisp raised
 * card and read recedes** to `--notif-read-surface`. An accent wash + left rail
 * on unread reads as a critical alert, and two of the three types are good
 * news. Tone lives only in the type tile.
 */
export function NotificationItem({
  type,
  title,
  body,
  timestamp,
  isRead,
  href,
  density = 'comfortable',
  isLeaving = false,
  onActivate,
  onDismiss,
  markReadLabel,
  dismissLabel,
}: NotificationItemProps) {
  const compact = density === 'compact';

  const handleActivate = (e: MouseEvent) => {
    // The dismiss button sits inside the row's box but outside its intent.
    if ((e.target as HTMLElement).closest('[data-row-exempt]')) return;
    onActivate?.();
  };

  const content: ReactNode = (
    <>
      <span className={cx(styles.tile, styles[type])} aria-hidden="true">
        <Icon name={TYPE_ICON[type]} size={compact ? 14 : 16} strokeWidth={2} />
      </span>
      <span className={styles.text}>
        <span className={styles.title}>{title}</span>
        {body ? <span className={styles.body}>{body}</span> : null}
        <span className={styles.meta}>
          {!isRead ? <span className={styles.dot} aria-hidden="true" /> : null}
          <span className={styles.time}>{timestamp}</span>
          {/* A linkless row has nowhere to navigate, so activating it marks
              read in place — this names that, rather than adding a second
              control that would fight the dismiss × for the corner. */}
          {!isRead && !href ? (
            <span className={styles.markReadHint}>{markReadLabel}</span>
          ) : null}
        </span>
      </span>
    </>
  );

  // The <li> IS the card — it carries surface, border, and the collapse. The
  // anchor can't contain the dismiss button (no nested interactives), so the
  // actions overlay the card instead of stealing a column from it.
  const rowClasses = cx(styles.row, compact ? styles.compact : styles.comfortable);

  return (
    <li
      className={cx(
        styles.item,
        compact ? styles.itemCompact : styles.itemComfortable,
        isRead ? styles.isRead : styles.isUnread,
        isLeaving && styles.itemLeaving,
      )}
    >
      {/* One activation rule for both densities: the row activates — it
          navigates when there's a link, and marks read either way. */}
      {href ? (
        <a href={href} className={rowClasses} onClick={handleActivate}>
          {content}
        </a>
      ) : (
        <button type="button" className={rowClasses} onClick={handleActivate}>
          {content}
        </button>
      )}

      <span className={styles.actions} data-row-exempt="">
        {onDismiss ? (
          <IconButton
            icon="x"
            label={dismissLabel}
            size={14}
            className={styles.dismiss}
            onClick={onDismiss}
          />
        ) : null}
      </span>
    </li>
  );
}
